
import { useScrollTop } from 'Core/hooks/useScrollTop';
import Page from 'Core/components/Page';
import TermsInput from 'Core/components/FormElements/TermsInput';
import { PrimaryButton } from 'Core/components/Button';
import Card from 'Core/components/FormElements/Card';
import { formatCurrency } from 'Core/utils/numberFormat';
import { Trans, useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { useGetPaymentInfo } from 'Core/hooks/platform/useGetPaymentInfo';
import useSubmitConsent from 'Modules/easyCredit/hooks/useSubmitConsent';
import useGetLatestRequest from 'Modules/easyCredit/hooks/useGetLatestRequest';
import Loader from 'Core/components/Loader';
import TermsModal from 'Modules/easyCredit/components/TermsModal';

const ConsentPage = () => {
    document.title = 'Payment Portal - Easy Credit';
    useScrollTop();
    const { t } = useTranslation();
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [requestId, setRequestId] = useState(null);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

    // Fetch payment info for displaying amount and getting customer identifiers
    const { data: paymentInfo, isLoading: isPaymentInfoLoading } = useGetPaymentInfo();

    const customerGid = paymentInfo?.data?.customer_gid || paymentInfo?.customer_gid;
    const externalReferenceId = paymentInfo?.data?.external_reference_id || paymentInfo?.external_reference_id;

    const submitConsentMutation = useSubmitConsent();
    
    // Poll for redirect link after consent submission
    const { data: latestRequest, isLoading: isPolling } = useGetLatestRequest(
        requestId,
        customerGid,
        externalReferenceId,
        !!requestId
    );

    // Redirect to EasyCredit when polling returns redirect link
    useEffect(() => {
        if (latestRequest?.data?.application?.easycredit_redirect_link?.redirect_link) {
            const redirectUrl = latestRequest.data.application.easycredit_redirect_link.redirect_link;
            console.log('Redirecting to Easy Credit after consent:', redirectUrl);
            window.location.replace(redirectUrl);
        }
    }, [latestRequest]);

    const handleAcceptOffer = async () => {
        if (!customerGid || !externalReferenceId) return;

        try {
            const result = await submitConsentMutation.mutateAsync({
                customerGid,
                externalReferenceId,
                acceptedTerms: t('easyCredit.consentContent.checkboxLabel'),
            });
            setRequestId(result.requestId);
        } catch (error) {
            console.error('Error submitting consent:', error);
        }
    };

    const handleTermsChange = (e) => {
        setTermsAccepted(e.target.checked);
    };

    // Show loader while processing
    if (isPaymentInfoLoading || submitConsentMutation.isLoading || isPolling) {
        return <Loader message={t('status.loading.processingRequest')} />;
    }

    const amount_to_pay = paymentInfo?.data?.amount_to_pay || paymentInfo?.amount_to_pay || 0;
    return (
        <Page title={t('easyCredit.page.financing.header')} withStepProgress={false} stickyFooter>
            <div className="max-w-5xl mx-auto">
                {/* Amount Card with modern design */}
                <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-solid border-green-200 shadow-md overflow-hidden !mb-3 md:!mb-4">
                    <div className="flex items-center gap-2">
                            <div className="flex-shrink-0 w-8 h-8 md:w-9 md:h-9 bg-emerald-500  rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs md:text-sm text-gray-600 font-medium mb-0">
                                    {t('easyCredit.amountToPay.label')}
                                </p>
                                <h3 className="!mb-0 text-xl md:text-2xl font-bold text-gray-900 tracking-tight">
                                    <Trans
                                        i18nKey={`{{amount}}`}
                                        values={{ amount: formatCurrency(amount_to_pay, { withDecimals: true }) }}
                                    />
                                </h3>
                            </div>
                        </div>
                </Card>

                {/* Consent Content Card */}
                <Card className="from-white border-solid border-gray-300 shadow-md overflow-hidden !mb-3 md:!mb-4 !py-">
                    <div className="">
                        <div className="flex items-center gap-2 mb-2.5 md:mb-3">
                            <div className="flex-shrink-0 w-8 h-8 md:w-9 md:h-9 bg-blue-500  rounded-lg flex items-center justify-center">
                                <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-sm md:text-base font-bold text-gray-900">
                                {t('easyCredit.consentContent.title')}
                            </h3>
                        </div>

                        <div className="bg-blue-50/50 rounded-lg p-2.5 md:p-3.5 mb-2.5 md:mb-3 border border-blue-100">
                            <p className="text-sm text-gray-700 leading-relaxed">
                                <Trans
                                    i18nKey="easyCredit.consentContent.para1"
                                    components={{
                                        termsLink: (
                                            <button
                                                type="button"
                                                onClick={() => setIsTermsModalOpen(true)}
                                                className="text-blue-600 hover:text-blue-800 underline font-medium cursor-pointer bg-transparent border-none p-0 inline"
                                            />
                                        ),
                                    }}
                                />
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-2.5 md:p-3.5 border border-amber-200">
                            <TermsInput name="terms" onChange={handleTermsChange}>
                                <span className="text-sm font-semibold text-gray-800">
                                    {t('easyCredit.consentContent.checkboxLabel')}
                                </span>
                            </TermsInput>
                        </div>
                    </div>
                </Card>

                {/* Action Button */}
                <PrimaryButton
                    block
                    data-testid="set-offer"
                    onClick={handleAcceptOffer}
                    disabled={!termsAccepted}
                    className="text-sm md:text-base shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                    <div className="flex items-center justify-center gap-2">
                        {t('easyCredit.consentContent.requestFinancingBtnLabel')}
                    </div>
                </PrimaryButton>
            </div>

            {/* Terms and Conditions Modal */}
            <TermsModal 
                isOpen={isTermsModalOpen} 
                onClose={() => setIsTermsModalOpen(false)} 
            />
        </Page >
    )
};

export default ConsentPage;
